// ENCUESTAS AL CLIENTE: las plantillas de fábrica y lo que comparten la
// pantalla del CRM y la página pública (Lissette 2026-10-15, Fase 1).
//
// Una plantilla NO se guarda en el servidor: "Usar plantilla" crea una
// encuesta de la agencia con una COPIA de su contenido, que luego se edita a
// gusto. Así una mejora aquí no le cambia las preguntas a una encuesta que ya
// se mandó.
//
// La forma del contenido es la que valida el servidor (contenidoSchema en
// deskte-api/src/utils/encuestas.ts):
//   { titulo:{es,en}, intro:{es,en}, gracias:{es,en},
//     preguntas:[{ id, tipo, texto:{es,en}, opciones?:[{es,en}], obligatoria }] }
//
// OJO: ninguna plantilla pregunta datos médicos ni el Seguro Social, y el
// editor avisa si alguien lo escribe (ver `pareceDatoSensible`).

export const TIPOS_DE_PREGUNTA = [
  { id: 'estrellas', es: '⭐ Estrellas 1–5', en: '⭐ Stars 1–5' },
  { id: 'escala10', es: '🔢 Escala 0–10', en: '🔢 Scale 0–10' },
  { id: 'unica', es: '⚪ Una opción', en: '⚪ Single choice' },
  { id: 'varias', es: '☑️ Varias opciones', en: '☑️ Multiple choice' },
  { id: 'sino', es: '✅ Sí / No', en: '✅ Yes / No' },
  { id: 'texto', es: '✏️ Respuesta libre', en: '✏️ Free text' },
];

/** El texto en el idioma pedido, o en el otro si ese está vacío. */
export function enIdioma(t, lang) {
  if (!t) return '';
  if (typeof t === 'string') return t;
  return (lang === 'es' ? t.es || t.en : t.en || t.es) || '';
}

let contador = 0;
export function nuevoIdDePregunta() {
  contador += 1;
  return `p${Date.now().toString(36)}${contador}`;
}

export function preguntaVacia(tipo = 'estrellas') {
  const p = { id: nuevoIdDePregunta(), tipo, texto: { es: '', en: '' }, obligatoria: false };
  if (tipo === 'unica' || tipo === 'varias') p.opciones = [{ es: '', en: '' }, { es: '', en: '' }];
  return p;
}

export function contenidoVacio() {
  return {
    titulo: { es: '', en: '' },
    intro: { es: '', en: '' },
    gracias: { es: '¡Gracias por tu tiempo! Tu opinión nos ayuda a mejorar.', en: 'Thank you for your time! Your feedback helps us improve.' },
    preguntas: [preguntaVacia('estrellas')],
  };
}

/**
 * Lo que una encuesta NO debe preguntar. Es un aviso, no un bloqueo: la
 * agencia decide, pero que lo decida sabiendo (HIPAA).
 */
const SENSIBLE = /(seguro social|social security|\bssn\b|diagn[oó]stic|medicament|medication|enfermedad|illness|condici[oó]n m[eé]dica|medical condition|embaraz|pregnan|n[uú]mero de p[oó]liza|policy number|tarjeta de cr[eé]dito|credit card|cuenta bancaria|bank account)/i;
export function pareceDatoSensible(texto) {
  return SENSIBLE.test(String(texto || ''));
}

const t = (es, en) => ({ es, en });
const GRACIAS = t(
  '¡Gracias por tu tiempo! Tu opinión nos ayuda a darte un mejor servicio.',
  'Thank you for your time! Your feedback helps us serve you better.',
);

export const PLANTILLAS_DE_ENCUESTA = [
  {
    id: 'satisfaccion',
    nombre: t('Satisfacción del servicio', 'Service satisfaction'),
    descripcion: t('Cómo se siente el cliente con la atención en general.', 'How the client feels about the service overall.'),
    contenido: {
      titulo: t('¿Cómo te atendimos?', 'How did we do?'),
      intro: t('Son solo 4 preguntas y nos ayudan muchísimo.', 'Just 4 quick questions — they help us a lot.'),
      gracias: GRACIAS,
      preguntas: [
        { id: 'general', tipo: 'estrellas', obligatoria: true, texto: t('En general, ¿qué tan satisfecho estás con nuestro servicio?', 'Overall, how satisfied are you with our service?') },
        { id: 'rapidez', tipo: 'estrellas', obligatoria: false, texto: t('¿Qué tan rápido te respondemos cuando nos necesitas?', 'How quickly do we respond when you need us?') },
        { id: 'claridad', tipo: 'sino', obligatoria: false, texto: t('¿Te explicamos tu póliza de forma clara?', 'Did we explain your policy clearly?') },
        { id: 'mejorar', tipo: 'texto', obligatoria: false, texto: t('¿Qué podemos mejorar?', 'What could we improve?') },
      ],
    },
  },
  {
    id: 'recomendacion',
    nombre: t('¿Nos recomendaría? (0–10)', 'Would you recommend us? (0–10)'),
    descripcion: t('La medida que usan las aseguradoras: promotores menos detractores.', 'The measure carriers use: promoters minus detractors.'),
    contenido: {
      titulo: t('Una pregunta rápida', 'One quick question'),
      intro: t('Tu respuesta nos dice si vamos por buen camino.', 'Your answer tells us whether we are on the right track.'),
      gracias: GRACIAS,
      preguntas: [
        { id: 'recomendaria', tipo: 'escala10', obligatoria: true, texto: t('Del 0 al 10, ¿qué tan probable es que nos recomiendes a un familiar o amigo?', 'From 0 to 10, how likely are you to recommend us to a friend or family member?') },
        { id: 'porque', tipo: 'texto', obligatoria: false, texto: t('¿Cuál es la razón principal de tu puntuación?', 'What is the main reason for your score?') },
      ],
    },
  },
  {
    id: 'poliza_nueva',
    nombre: t('Después de una póliza nueva', 'After a new policy'),
    descripcion: t('Cómo fue el proceso de inscripción.', 'How the enrollment process went.'),
    contenido: {
      titulo: t('¿Cómo fue tu inscripción?', 'How was your enrollment?'),
      intro: t('Queremos saber cómo te fue al sacar tu póliza con nosotros.', 'We would like to know how getting your policy with us went.'),
      gracias: GRACIAS,
      preguntas: [
        { id: 'proceso', tipo: 'estrellas', obligatoria: true, texto: t('¿Qué tan fácil fue el proceso de inscripción?', 'How easy was the enrollment process?') },
        { id: 'entendio', tipo: 'sino', obligatoria: false, texto: t('¿Entiendes bien lo que cubre tu póliza?', 'Do you understand what your policy covers?') },
        { id: 'tarjeta', tipo: 'sino', obligatoria: false, texto: t('¿Ya recibiste tu tarjeta del seguro?', 'Have you received your insurance card?') },
        { id: 'dudas', tipo: 'texto', obligatoria: false, texto: t('¿Te quedó alguna duda que podamos aclarar?', 'Is there anything we can clarify for you?') },
      ],
    },
  },
  {
    id: 'renovacion',
    nombre: t('Renovación', 'Renewal'),
    descripcion: t('Si piensa seguir con nosotros y si hubo cambios en su vida.', 'Whether they plan to stay and if anything changed.'),
    contenido: {
      titulo: t('Se acerca tu renovación', 'Your renewal is coming up'),
      intro: t('Ayúdanos a preparar tu renovación con estas preguntas.', 'Help us prepare your renewal with these questions.'),
      gracias: GRACIAS,
      preguntas: [
        { id: 'seguir', tipo: 'unica', obligatoria: true, texto: t('¿Piensas renovar tu póliza con nosotros?', 'Do you plan to renew your policy with us?'), opciones: [t('Sí, seguro', 'Yes, definitely'), t('Probablemente', 'Probably'), t('No estoy seguro', 'Not sure'), t('No', 'No')] },
        { id: 'cambios', tipo: 'varias', obligatoria: false, texto: t('¿Hubo algún cambio este año? (marca los que apliquen)', 'Did anything change this year? (check all that apply)'), opciones: [t('Cambio de trabajo o ingresos', 'Job or income change'), t('Me casé o divorcié', 'Married or divorced'), t('Nuevo miembro en la familia', 'New family member'), t('Me mudé', 'I moved'), t('Ninguno', 'None')] },
        { id: 'llamada', tipo: 'sino', obligatoria: false, texto: t('¿Quieres que te llamemos para revisar tus opciones?', 'Would you like us to call you to review your options?') },
      ],
    },
  },
  {
    id: 'otros_seguros',
    nombre: t('Interés en otros seguros', 'Interest in other coverage'),
    descripcion: t('Descubre qué más le puedes ofrecer a cada cliente.', 'Find out what else you can offer each client.'),
    contenido: {
      titulo: t('¿Podemos ayudarte con algo más?', 'Can we help you with anything else?'),
      intro: t('Además de tu póliza actual, ofrecemos otros seguros.', 'Besides your current policy, we offer other coverage.'),
      gracias: GRACIAS,
      preguntas: [
        // `interes` = el tipo de póliza de crm-app/policyTypes.js: con la regla
        // encendida, marcarla crea un lead con ese interés (Fase 2).
        { id: 'interes', tipo: 'varias', obligatoria: true, texto: t('¿Qué otros seguros te interesaría conocer?', 'Which other coverage would you like to learn about?'), opciones: [{ ...t('Vida', 'Life'), interes: 'life' }, { ...t('Dental', 'Dental'), interes: 'dental' }, { ...t('Visión', 'Vision'), interes: 'vision' }, { ...t('Auto', 'Auto'), interes: 'auto' }, { ...t('Casa', 'Home'), interes: 'home' }, t('Ninguno por ahora', 'None for now')] },
        { id: 'contacto', tipo: 'unica', obligatoria: false, texto: t('¿Cómo prefieres que te contactemos?', 'How would you prefer we contact you?'), opciones: [t('Llamada', 'Phone call'), t('Mensaje de texto', 'Text message'), t('Correo', 'Email'), t('WhatsApp', 'WhatsApp')] },
      ],
    },
  },
  {
    id: 'agente',
    nombre: t('Atención del agente', 'Agent service'),
    descripcion: t('Cómo trató el agente al cliente.', 'How the agent treated the client.'),
    contenido: {
      titulo: t('Cuéntanos sobre tu agente', 'Tell us about your agent'),
      intro: t('Tu opinión es confidencial y nos ayuda a mejorar el equipo.', 'Your feedback is confidential and helps us improve the team.'),
      gracias: GRACIAS,
      preguntas: [
        { id: 'trato', tipo: 'estrellas', obligatoria: true, texto: t('¿Cómo calificarías el trato de tu agente?', 'How would you rate your agent’s service?') },
        { id: 'conocimiento', tipo: 'estrellas', obligatoria: false, texto: t('¿Qué tan bien te supo explicar tus opciones?', 'How well did they explain your options?') },
        { id: 'disponible', tipo: 'sino', obligatoria: false, texto: t('¿Estuvo disponible cuando lo necesitaste?', 'Were they available when you needed them?') },
        { id: 'comentario', tipo: 'texto', obligatoria: false, texto: t('¿Algún comentario sobre tu agente?', 'Any comments about your agent?') },
      ],
    },
  },
];

/**
 * Las reglas automáticas de una encuesta nueva (Fase 2). Mismo valor por
 * defecto que el servidor (reglasSchema en deskte-api/src/utils/encuestas.ts):
 * tarea y lead encendidos, envío automático apagado.
 */
export function reglasPorDefecto() {
  return {
    tareaSiBaja: true,
    leadSiInteres: true,
    envioAuto: { activo: false, dias: 30, asunto: { es: '', en: '' }, mensaje: { es: '', en: '' } },
  };
}

/** Las reglas guardadas encima de las de por defecto (lo que falte, por defecto). */
export function reglasCompletas(r) {
  const base = reglasPorDefecto();
  const x = r && typeof r === 'object' ? r : {};
  return { ...base, ...x, envioAuto: { ...base.envioAuto, ...(x.envioAuto || {}) } };
}

/** Copia profunda: la encuesta nueva no comparte objetos con la plantilla. */
export function contenidoDePlantilla(plantilla) {
  return JSON.parse(JSON.stringify(plantilla.contenido));
}

/** Cómo se lee una respuesta guardada, para la tabla y el CSV. */
export function textoDeRespuesta(pregunta, valor, lang) {
  if (valor === undefined || valor === null || valor === '') return '';
  const es = lang === 'es';
  switch (pregunta.tipo) {
    case 'estrellas': return `${'★'.repeat(Number(valor) || 0)} (${valor}/5)`;
    case 'escala10': return `${valor}/10`;
    case 'sino': return valor === 'si' ? (es ? 'Sí' : 'Yes') : 'No';
    case 'unica': return enIdioma((pregunta.opciones || [])[valor], lang);
    case 'varias': return (Array.isArray(valor) ? valor : []).map((i) => enIdioma((pregunta.opciones || [])[i], lang)).filter(Boolean).join(', ');
    default: return String(valor);
  }
}
